/**
 * stage-color — the five-stop stage ramp, snapped, never interpolated.
 *
 * The handoff is explicit about this: "Do not interpolate new hexes." Eight
 * agents land on five stops via `round(i / (n - 1) × 4)`, so two pairs of
 * agents deliberately share a stop — that sharing is the point of snapping,
 * not a rounding artefact to design around.
 *
 * The handoff refers to this helper as `snappedStageColor` in
 * `lib/ui-state.ts`; it did not exist in this codebase (it lives on the
 * Riverflow branch), so it is written here, pure and tested, rather than
 * having each caller do its own arithmetic on the ramp.
 */

/** `#D4F1F4 → #A7DFE8 → #8FA998 → #5FA8B0 → #2B6777` */
export const STAGE_STOPS = [
  "var(--stage-1)",
  "var(--stage-2)",
  "var(--stage-3)",
  "var(--stage-4)",
  "var(--stage-5)",
] as const;

/**
 * The stop index for step `i` of `n`. A single-step pipeline gets the last
 * stop — it is finished by definition, not at the start of a ramp.
 */
export function snappedStageIndex(i: number, n: number): number {
  if (n <= 1) return STAGE_STOPS.length - 1;
  const clamped = Math.min(Math.max(i, 0), n - 1);
  return Math.round((clamped / (n - 1)) * (STAGE_STOPS.length - 1));
}

/** The CSS colour for step `i` of `n`, as a custom-property reference. */
export function snappedStageColor(i: number, n: number): string {
  return STAGE_STOPS[snappedStageIndex(i, n)];
}
